/**
 * REST API base (must end with /api/v1 for this codebase).
 * - Dev: localhost backend unless VITE_API_URL is set.
 * - Prod: same-origin /api/v1 when unset (nginx must proxy /api → Node).
 */
function normalizeApiBase(raw: string): string {
  return raw.replace(/\/$/, '');
}

const fromEnv =
  typeof import.meta.env.VITE_API_URL === 'string'
    ? import.meta.env.VITE_API_URL.trim()
    : '';

export const API_BASE_URL = fromEnv
  ? normalizeApiBase(fromEnv)
  : import.meta.env.DEV
  ? 'http://localhost:5002/api/v1'
  : '/api/v1';

/**
 * When API_BASE_URL is relative (e.g. VITE_API_URL=/api/v1), API calls go through the Vite proxy,
 * but `/uploads` is NOT under `/api` — the SPA origin would 404 for `/uploads/...`.
 * In dev, default uploads to the Node server; in prod, same origin (nginx should proxy /uploads).
 * Override: VITE_PUBLIC_ASSETS_ORIGIN=http://host:port
 */
function uploadsOriginWhenApiBaseIsRelative(): string {
  const explicit =
    typeof import.meta.env.VITE_PUBLIC_ASSETS_ORIGIN === 'string'
      ? import.meta.env.VITE_PUBLIC_ASSETS_ORIGIN.trim().replace(/\/$/, '')
      : '';
  if (explicit) return explicit;
  if (import.meta.env.DEV) {
    return 'http://localhost:5002';
  }
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
}

/** Origin for absolute URLs to uploaded/static paths when API uses relative base (same host as SPA). */
export function apiOriginForStaticAssets(): string {
  const base = API_BASE_URL;
  if (base.startsWith('http://') || base.startsWith('https://')) {
    return base.replace(/\/api\/v1\/?$/, '');
  }
  return uploadsOriginWhenApiBaseIsRelative();
}

/**
 * Stored paths like `/uploads/users/.../avatar.png` must use the API host in dev (SPA on :3000, API on :5002).
 * Use for `<img src>`, `<Avatar src>`, etc.
 */
export function resolvePublicAssetUrl(path?: string | null): string | undefined {
  if (path == null || !String(path).trim()) return undefined;
  const s = String(path).trim();
  if (/^https?:\/\//i.test(s)) return s;
  const origin = apiOriginForStaticAssets();
  const rel = s.startsWith('/') ? s : `/${s}`;
  if (!origin) return rel;
  return `${origin}${rel}`;
}
